import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {InsightsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import AddQueryToAnalyticsDashboardModal from 'insights/analytics-dashboards/AddQueryToAnalyticsDashboardModal';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import CreateAnalyticsDashboardModal from 'insights/sidepanel/CreateAnalyticsDashboardModal';
import {DEFAULT_QUERY_DESCRIPTION} from 'insights/warehouse/AnalyticsWarehouseConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

export const CREATE_DASHBOARD_BUTTON_TEXT = 'Create dashboard';
export const ADD_TO_EXISTING_OPTION_TEXT = 'Add to existing dashboard';
export const CREATE_NEW_OPTION_TEXT = 'Create new dashboard';
export const BLOCK_NAME_FROM_QUERY = 'Block 1';

export function canUserCreateOrEditInsightsDashboards(): boolean {
  return (
    isFeatureEnabled({feature: InsightsFeature.INSIGHTS_CREATE}) ||
    isFeatureEnabled({feature: InsightsFeature.INSIGHTS_EDIT})
  );
}

export function buildAnalyticsQueryFromSQL(sqlQuery: string): AnalyticsQuerySpecRequest {
  return {
    name: BLOCK_NAME_FROM_QUERY,
    description: DEFAULT_QUERY_DESCRIPTION,
    sqlQuery,
  };
}

export interface CreateDashboardFromQueryButtonProps {
  sqlQuery: string;
  className?: string;
}

export default function CreateDashboardFromQueryButton(
  props: CreateDashboardFromQueryButtonProps
): JSX.Element | null {
  const [isOpen, setIsOpen] = React.useState(false);

  const canCreateNew = isFeatureEnabled({feature: InsightsFeature.INSIGHTS_CREATE});
  const canAddToExisting = isFeatureEnabled({feature: InsightsFeature.INSIGHTS_EDIT});

  if (!canCreateNew && !canAddToExisting) {
    return null;
  }

  const analyticsQuery = buildAnalyticsQueryFromSQL(props.sqlQuery);

  function handleAddToExisting(): void {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_ADD_QUERY_TO_EXISTING_DASHBOARD);
    legacyRunModal(AddQueryToAnalyticsDashboardModal, {analyticsQuery});
  }

  function handleCreateNew(): void {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_CREATE_NEW_DASHBOARD_FROM_QUERY);
    legacyRunModal(CreateAnalyticsDashboardModal, {initialAnalyticsQueries: [analyticsQuery]});
  }

  function handleDropdownToggle(nextIsOpen: boolean): void {
    if (nextIsOpen) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_CREATE_DASHBOARD_FROM_QUERY);
    }
    setIsOpen(nextIsOpen);
  }

  const menu = (
    <Menu width={MenuWidth.MEDIUM}>
      {canAddToExisting && (
        <Option
          icon={ModelTypeConstants.ANALYTICS_DASHBOARD}
          onSelect={handleAddToExisting}
          dataTestAttrs={{component: 'CreateDashboardFromQueryButton', key: 'addToExisting'}}
        >
          {ADD_TO_EXISTING_OPTION_TEXT}
        </Option>
      )}
      {canCreateNew && (
        <Option
          icon="compose"
          onSelect={handleCreateNew}
          dataTestAttrs={{component: 'CreateDashboardFromQueryButton', key: 'createNew'}}
        >
          {CREATE_NEW_OPTION_TEXT}
        </Option>
      )}
    </Menu>
  );

  return (
    <Dropdown menu={menu} isOpen={isOpen} onToggle={handleDropdownToggle}>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        className={props.className}
        icon="compose"
        size={ButtonSize.SMALL}
        text={CREATE_DASHBOARD_BUTTON_TEXT}
        dataTestAttrs={{component: 'CreateDashboardFromQueryButton', element: 'root'}}
      />
    </Dropdown>
  );
}
